import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../services/api";

const AuthContext = createContext(null);
const STORAGE_KEY = "lumigo_auth";

const safeParse = (v) => {
    try { return JSON.parse(v); } catch { return null; }
};

export const AuthProvider = ({ children }) => {
    const stored = safeParse(localStorage.getItem(STORAGE_KEY)) || safeParse(localStorage.getItem("watchstore_auth"));
    const [token, setToken] = useState(stored?.token || null);
    const [user, setUser] = useState(stored?.user || null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isAuthenticated = !!token;

    const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const [lastActivity, setLastActivity] = useState(Date.now());

    const persist = (t, u) => {
        setToken(t);
        setUser(u);
        setLastActivity(Date.now());
        if (!t) {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem("lumigo_last_activity");
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: t, user: u }));
            localStorage.setItem("lumigo_last_activity", Date.now().toString());
        }
    };

    const logout = (opts = {}) => {
        const isAdmin = user?.role === "admin" || user?.role === "superadmin";
        persist(null, null);
        if (opts.sessionExpired) {
            const redirectUrl = isAdmin ? "/admin-login?sessionExpired=true" : "/login?sessionExpired=true";
            window.location.href = redirectUrl;
        } else {
            window.location.href = "/";
        }
    };

    // Track user activity (clicks, keys, touch) to enforce 30-minute inactivity timeout
    useEffect(() => {
        if (!token) return;

        let throttleTimer = null;
        const updateActivity = () => {
            if (!throttleTimer) {
                throttleTimer = setTimeout(() => {
                    setLastActivity(Date.now());
                    localStorage.setItem("lumigo_last_activity", Date.now().toString());
                    throttleTimer = null;
                }, 10000); // Throttle activity updates to every 10s
            }
        };

        const events = ["mousedown", "keydown", "scroll", "touchstart"];
        events.forEach((ev) => window.addEventListener(ev, updateActivity, { passive: true }));

        // Check for 30 minute expiry every 20 seconds
        const checkInterval = setInterval(() => {
            const storedTime = parseInt(localStorage.getItem("lumigo_last_activity") || "0", 10) || lastActivity;
            const elapsed = Date.now() - storedTime;

            if (elapsed >= INACTIVITY_TIMEOUT_MS) {
                clearInterval(checkInterval);
                logout({ sessionExpired: true });
            }
        }, 20000);

        return () => {
            events.forEach((ev) => window.removeEventListener(ev, updateActivity));
            clearInterval(checkInterval);
            if (throttleTimer) clearTimeout(throttleTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, user]);

    const login = async ({ email, password }) => {
        setError(null);
        try {
            const { data } = await api.post("/api/users/login", { email, password });
            persist(data.token, data);
            return { ok: true, data };
        } catch (err) {
            const msg = err?.response?.data?.message || "Login failed";
            setError(msg);
            return { ok: false, code: err?.response?.data?.code, message: msg };
        }
    };

    const register = async ({ name, email, password }) => {
        setError(null);
        try {
            const { data } = await api.post("/api/users/register", { name, email, password });
            persist(data.token, data);
            return { ok: true };
        } catch (err) {
            const msg = err?.response?.data?.message || "Registration failed";
            setError(msg);
            return { ok: false, message: msg };
        }
    };

    const googleLogin = async (credential) => {
        setError(null);
        try {
            const { data } = await api.post("/api/users/google", { credential });
            persist(data.token, data);
            return { ok: true };
        } catch (err) {
            const msg = err?.response?.data?.message || "Google login failed";
            setError(msg);
            return { ok: false, code: err?.response?.data?.code, message: msg };
        }
    };

    // refresh: optional later (/me)
    useEffect(() => {
        setLoading(false);
    }, []);

    const setSession = (t, u) => {
        persist(t, u);
    };

    const value = useMemo(
        () => ({ user, token, loading, error, isAuthenticated, login, register, googleLogin, logout, setSession }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user, token, loading, error, isAuthenticated]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
